import { useEffect, useRef, useState } from "react";
import type { DragEvent } from "react";
import { moveItem, sameOrder } from "./reorder";

export type DragHandleProps = { draggable: boolean; onDragStart: (event: DragEvent) => void; onDragEnd: () => void };
export type DragRowProps = { onDragOver: (event: DragEvent) => void };
export type DragReorder = {
  dragIndex: number | null;
  /** Spread on the drag grip (or on the whole tile when it has no inputs) */
  handleProps: (index: number) => DragHandleProps;
  /** Spread on each row: hovering a row with the dragged one moves it there */
  rowProps: (index: number) => DragRowProps;
};
export type PersistedOrder<T> = DragReorder & {
  list: T[];
  saving: boolean;
  /** Move up (-1) / down (1) one place: the arrows */
  step: (index: number, direction: -1 | 1) => void;
};

/** HTML5 drag between rows (desktop; phones use the arrows). Rows move under the pointer; `onDrop` runs once at the end. */
export function useDragReorder(onMove: (from: number, to: number) => void, onDrop: () => void): DragReorder {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragging = useRef<number | null>(null);
  return {
    dragIndex,
    handleProps: (index) => ({
      draggable: true,
      onDragStart: (event) => {
        event.dataTransfer.effectAllowed = "move";
        // Firefox only starts a drag that carries data
        event.dataTransfer.setData("text/plain", String(index));
        dragging.current = index;
        setDragIndex(index);
      },
      onDragEnd: () => {
        if (dragging.current === null) return;
        dragging.current = null;
        setDragIndex(null);
        onDrop();
      },
    }),
    rowProps: (index) => ({
      onDragOver: (event) => {
        const from = dragging.current;
        // A drag that another list started (nested lists) is not ours
        if (from === null) return;
        event.preventDefault();
        if (from === index) return;
        onMove(from, index);
        dragging.current = index;
        setDragIndex(index);
      },
    }),
  };
}

/**
 * A list the admin reorders (arrows or drag) that is saved to the server: the move shows straight away and is put
 * back if the save fails. `source` must be stable (state or useMemo): a new copy from the server replaces the order.
 */
export function usePersistedOrder<T>(
  source: T[],
  getId: (item: T) => string,
  save: (ids: string[]) => Promise<unknown>,
  onError: (err: any) => void,
  onSaved?: (next: T[]) => void
): PersistedOrder<T> {
  const [list, setListState] = useState<T[]>(source);
  const [saving, setSaving] = useState(false);
  const listRef = useRef(list);
  const savingRef = useRef(false);
  const dragStart = useRef<T[] | null>(null);

  const setList = (next: T[]) => {
    listRef.current = next;
    setListState(next);
  };

  useEffect(() => {
    listRef.current = source;
    setListState(source);
  }, [source]);

  const persist = async (next: T[], previous: T[]) => {
    const ids = next.map(getId);
    if (sameOrder(ids, previous.map(getId))) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await save(ids);
      onSaved?.(next);
    } catch (err) {
      setList(previous);
      onError(err);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const step = (index: number, direction: -1 | 1) => {
    const previous = listRef.current;
    const target = index + direction;
    if (savingRef.current || target < 0 || target >= previous.length) return;
    const next = moveItem(previous, index, target);
    setList(next);
    void persist(next, previous);
  };

  const drag = useDragReorder(
    (from, to) => {
      if (savingRef.current) return;
      if (!dragStart.current) dragStart.current = listRef.current;
      setList(moveItem(listRef.current, from, to));
    },
    () => {
      const previous = dragStart.current;
      dragStart.current = null;
      if (previous) void persist(listRef.current, previous);
    }
  );

  return { list, saving, step, ...drag };
}
